import { ApiError } from '../api/client.ts';
import { i18n } from '../i18n/index.ts';
import { useToastStore } from '../stores/toast.ts';

export interface ApiErrorDetail {
  code?: string;
  traceId?: string;
  message: string;
}

function t(path: string, values?: Record<string, unknown>): string {
  return (values ? i18n.global.t(path, values) : i18n.global.t(path)) as string;
}

// 主行：中文可读文案。后端/前端错误码优先映射到 error.* 目录，否则按状态码兜底。
export function formatApiError(cause: unknown): string {
  if (cause instanceof SyntaxError) return t('error.SYNTAX_JSON');
  if (cause instanceof ApiError) {
    const key = `error.${cause.code}`;
    if (cause.code && cause.code !== 'REQUEST_FAILED' && i18n.global.te(key)) return i18n.global.t(key) as string;
    return cause.status === 0 || cause.status >= 500 || cause.status === 429 ? t('error.GENERIC_500') : t('error.GENERIC_400');
  }
  return cause instanceof Error ? cause.message : String(cause);
}

export interface SettingsErrorOptions {
  includeTrace?: boolean;
  fieldLabel?: (field: string) => string;
}

function settingsFieldLabel(field: string, options: SettingsErrorOptions): string {
  if (field === '$') return t('settings.validation.rootField');
  return options.fieldLabel?.(field) || field;
}

export function formatSettingsError(cause: unknown, options: SettingsErrorOptions = {}): string {
  const value = cause && typeof cause === 'object' ? cause as Record<string, unknown> : {};
  const candidate = typeof value.code === 'string' ? value.code : '';
  const code = /^[A-Z][A-Z0-9_]{0,127}$/.test(candidate) ? candidate : '';
  const status = typeof value.status === 'number' ? value.status : undefined;
  let message: string;
  if (code === 'SETTINGS_VALIDATION' && Array.isArray(value.fields) && value.fields.length) {
    const details = value.fields.map(entry => {
      const issue = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {};
      const field = typeof issue.field === 'string' && issue.field ? issue.field : '$';
      const itemId = typeof issue.item_id === 'string' && issue.item_id ? issue.item_id + ' · ' : '';
      const reasonCode = typeof issue.code === 'string' ? issue.code : '';
      const reasonKey = `settings.validation.${reasonCode}`;
      const reason = reasonCode && i18n.global.te(reasonKey) ? t(reasonKey) : t('settings.validation.invalid');
      return `${itemId}${settingsFieldLabel(field, options)}: ${reason}`;
    }).join('; ');
    message = t('settings.validation.rejected', { details });
  } else {
    const key = code ? `settings.errors.${code}` : '';
    const fallback = status === 0 || (status ?? 400) >= 500 || status === 429 ? 'settings.errors.UNKNOWN_SERVICE' : 'settings.errors.UNKNOWN_REQUEST';
    message = key && i18n.global.te(key) ? t(key) : t(fallback);
  }
  const coded = code ? `${message} [${code}]` : message;
  const trace = typeof value.traceId === 'string' ? value.traceId : typeof value.trace_id === 'string' ? value.trace_id : '';
  return options.includeTrace !== false && trace ? `${coded} · ${t('common.traceId')}: ${trace}` : coded;
}

export function notifyApiError(cause: unknown, message?: string): string {
  const text = message ?? formatApiError(cause);
  useToastStore().error(text);
  return text;
}

// 可展开诊断：保留错误码、追踪号与后端原始（英文）消息，便于排障。
export function apiErrorDetail(cause: unknown): ApiErrorDetail | null {
  if (cause instanceof ApiError) return { code: cause.code, traceId: cause.traceId, message: cause.message };
  if (cause instanceof Error) return { message: cause.message };
  return cause == null ? null : { message: String(cause) };
}
